/**
 * Shared body primitives for the per-type renderers (ARCH-005 §9).
 *
 * These render INSIDE the body slot only. They never render the meta-note,
 * Sources line, or video. Those three are DeliverableLayout's job and the
 * single visual contract (ARCH-005 §3 / Don't). Keeping the small section
 * head + field list here means every per-type renderer stays visually aligned
 * inside the layout body without each one re-deriving spacing.
 *
 * `Verbosity` is the coaching-depth tier (AI-12, PR-45). Each renderer
 * interprets it per ARCH-005 §5: 'recommend' tightens, 'show_me' is full,
 * 'build_it_with_me' adds methodology callouts. We apply a light touch
 * (collapse secondary detail at 'recommend') rather than diverging wildly so
 * two renderers never disagree wildly on the same tier.
 */

export type Verbosity = 'recommend' | 'show_me' | 'build_it_with_me';

export interface RendererProps<T> {
  payload: T;
  verbosity: Verbosity;
}

export function SectionHead({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 font-display text-2xl italic leading-tight text-[color:var(--color-black)]">
      {children}
    </h2>
  );
}

export function FieldList({
  title,
  items,
}: {
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
        {title}
      </p>
      <ul className="list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-[color:var(--color-black)]">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Methodology callout shown only at the 'build_it_with_me' verbosity tier
 * (ARCH-005 §5). Renders a "do it yourself next time" style aside inside the
 * body. This is NOT the meta-note (that is layout-level); it is the per-renderer
 * teaching layer the build-it tier opts into.
 */
export function MethodologyCallout({
  verbosity,
  children,
}: {
  verbosity: Verbosity;
  children: React.ReactNode;
}) {
  if (verbosity !== 'build_it_with_me') return null;
  return (
    <aside className="rounded-lg border border-[color:var(--color-rose)] bg-[color:var(--color-rose-subtle2)] p-4 text-sm leading-relaxed text-[color:var(--color-black)]">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-rose-dark)]">
        How to do this yourself
      </p>
      {children}
    </aside>
  );
}
